import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import { useLanguageStore } from '../../stores/language-store';
import { t } from '../../i18n/translations';

type VersionMeta = {
  id: string;
  comment?: string;
};

interface VersionPickerProps {
  meta: VersionMeta[];
  valueId: string;
  onChange: (id: string) => void | Promise<void>;
}

export function VersionPicker({ meta, valueId, onChange }: VersionPickerProps) {
  const language = useLanguageStore((s) => s.language);
  const [open, setOpen] = useState(false);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const selectedIndex = useMemo(() => {
    const idx = meta.findIndex((m) => m.id === valueId);
    return idx >= 0 ? idx : Math.max(0, meta.length - 1);
  }, [meta, valueId]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (!root.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const hoverEntry = hoverId ? meta.find((m) => m.id === hoverId) : null;
  const hoverComment = (hoverEntry?.comment ?? '').trim() || t(language, 'noComment');

  return (
    <div ref={rootRef} className="relative inline-flex items-center">
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-text hover:bg-surface-lighter transition-colors"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="tabular-nums">{selectedIndex + 1}</span>
        <ChevronDown size={14} className={clsx('text-text-muted transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 z-50">
          <div className="relative">
            <div
              role="listbox"
              className="min-w-[72px] overflow-hidden rounded-lg border border-surface-lighter bg-surface-light shadow-xl py-1"
            >
              {meta.map((m, idx) => {
                const active = m.id === valueId;
                return (
                  <button
                    key={m.id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onMouseEnter={() => setHoverId(m.id)}
                    onMouseLeave={() => setHoverId((cur) => (cur === m.id ? null : cur))}
                    onClick={async () => {
                      setOpen(false);
                      setHoverId(null);
                      await onChange(m.id);
                    }}
                    className={clsx(
                      'w-full px-3 py-2 text-left text-xs tabular-nums transition-colors',
                      active ? 'bg-accent/20 text-accent' : 'text-text hover:bg-surface-lighter'
                    )}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>

            {hoverId && (
              <div className="absolute left-full top-0 ml-2 w-64 rounded-lg border border-surface-lighter bg-surface-light shadow-xl p-3">
                <div className="text-[11px] font-semibold text-text-muted mb-1">{t(language, 'versionComment')}</div>
                <div className="text-xs text-text whitespace-pre-wrap break-words">{hoverComment}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

